/**
 * APP-6672 — explicit allowlist of left-sidebar nav items that remain usable
 * while the app is offline (per Marc's "Anything that isn't available offline
 * should be DISABLED" design annotation, FR-002/004/005/006a).
 *
 * The offline-recording MVP keeps exactly one capability live offline:
 * recording a new session (the Scribe entry point + viewing the in-progress
 * offline session being recorded). Every other sidebar destination is backed by
 * a network call, so it must be visibly greyed + non-interactive + carry a "Not
 * available offline" tooltip when {@link useOfflineGating}'s `shouldGateControls`
 * is true.
 *
 * Gating is keyed on the nav item's stable `testId` and applied centrally inside
 * `NavItem`. The list is an ALLOWLIST (not a blocklist) on purpose: a nav item
 * rendered through `NavItem` is gated-by-default offline until someone
 * deliberately adds its `testId` here.
 *
 * The allowlist only holds for items that actually route through `NavItem`. A
 * component rendering its own `SidebarMenuButton` never reaches this check and
 * stays live offline — which is exactly how the Integrations item once shipped
 * fully interactive offline. `nav-items-render-via-nav-item.test.ts` now fails
 * the build on that bypass, which is what makes "gated by default" true rather
 * than merely intended.
 */

/**
 * `testId`s of the nav items that stay fully enabled while offline.
 *
 * - `sidebar-link-view-sessions` — Scribe (the recording entry point + the
 *   in-progress session list/detail).
 * - `sidebar-link-open-last-session` — Scribe entry rendered during an EHR
 *   launch (opens the launched session directly).
 * - `sidebar-link-new-session` — the "New session" button (the other recording
 *   entry point).
 */
export const OFFLINE_AVAILABLE_NAV_TEST_IDS = [
  'sidebar-link-view-sessions',
  'sidebar-link-open-last-session',
  'sidebar-link-new-session',
] as const;

const offlineAvailableNavTestIds = new Set<string>(OFFLINE_AVAILABLE_NAV_TEST_IDS);

/**
 * Whether a nav item (identified by its `testId`) is available offline.
 * Items with no `testId` are treated as gated — a deliberate item must opt in.
 */
export const isNavItemAvailableOffline = (testId: string | undefined): boolean =>
  testId !== undefined && offlineAvailableNavTestIds.has(testId);
